/** Виджеты вьюера: единый рельс инструментов карты (правый край, вертикально —
 *  подложка, этажи, 3D, замер, A→B, каталог, легенда) и подсказка пустого
 *  состояния. Один контейнер вместо трёх блоков с захардкоженными top: ничего
 *  не перекрывается (редизайн UX 26.07). Иконки — SVG-глифы Aurora, не текст:
 *  символьные глифы (◐ ◆ ⟟) на светлой теме читались как чёрные кляксы. */
import { getLang, tr } from '@app/i18n'
import { localized } from '@app/rvf'
import { useEffect, useRef, useState } from 'preact/hooks'
import {
  a2bOpen,
  activeLevelId,
  BASEMAP_MODES,
  type BasemapChoice,
  basemapMode,
  catalogOpen,
  langTick,
  legendOpen,
  measureActive,
  measurePoints,
  qrPassTargetId,
  savedMapAvailable,
  venue,
  venueMap,
  view3D,
} from '../state'
import {
  GLYPH_A2B,
  GLYPH_CATALOG,
  GLYPH_CUBE_3D,
  GLYPH_FLOORS,
  GLYPH_LAYERS,
  GLYPH_LEGEND,
  GLYPH_QR,
  GLYPH_RULER,
} from './glyphs'
import { VENUE_QR_TARGET } from './qr-pass'

/** Значок стиля подложки в списке (маленький, рядом с названием). */
const STYLE_GLYPH: Record<BasemapChoice, string> = {
  dark: '◐',
  cad: '○',
  liberty: '◉',
  bright: '◎',
  fiord: '◍',
  saved: '⬇',
}

/** Меню стилей карты (5 стилей OpenFreeMap); выбор живёт на устройстве.
 *  Экспорт: используется и на аварийной карте (фидбек владельца 26.07). */
export function BasemapMenu({ onClose }: { onClose: () => void }) {
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: PointerEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [onClose])
  const cur = basemapMode.value
  return (
    <div ref={boxRef} class="basemap-menu glass" role="listbox" aria-label={tr('basemap.title')}>
      <p class="tech-label basemap-menu-title">{tr('basemap.title')}</p>
      {BASEMAP_MODES.filter((m) => m !== 'saved' || savedMapAvailable.value).map((m) => (
        <button
          key={m}
          type="button"
          role="option"
          aria-selected={m === cur}
          class={`lang-item${m === cur ? ' lang-item-on' : ''}`}
          onClick={() => {
            if (m !== basemapMode.value) {
              basemapMode.value = m
              try {
                localStorage.setItem('basemap', m)
              } catch {
                // приватный режим — выбор просто не сохранится
              }
              venueMap.value?.setBasemapMode(m)
            }
            onClose()
          }}
        >
          <span>
            <span aria-hidden="true" style={{ marginRight: 8 }}>
              {STYLE_GLYPH[m]}
            </span>
            {tr(`basemap.style_${m}`)}
          </span>
          {m === cur && <span aria-hidden="true">✓</span>}
        </button>
      ))}
    </div>
  )
}

function RailBtn(props: { glyph: string; title: string; on?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      class={`rail-btn${props.on ? ' rail-btn-on' : ''}`}
      title={props.title}
      aria-label={props.title}
      onClick={props.onClick}
    >
      {/* Глиф — доверенная строка из glyphs.ts (наш же модуль), не ввод. */}
      <span class="glyph" aria-hidden="true" dangerouslySetInnerHTML={{ __html: props.glyph }} />
    </button>
  )
}

/** Меню этажей (в рельсе, вместо плавающего блока — он перекрывал
 *  инструменты на низких экранах; фидбек владельца 26.07). */
function FloorsMenu({ onClose }: { onClose: () => void }) {
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: PointerEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [onClose])
  const v = venue.value
  if (!v) return null
  const active = activeLevelId.value
  const pick = (levelId: string | null): void => {
    activeLevelId.value = levelId
    venueMap.value?.setLevel(levelId)
    onClose()
  }
  const levels = [...v.levels].sort((a, b) => b.ordinal - a.ordinal)
  return (
    <div ref={boxRef} class="basemap-menu glass" role="listbox" aria-label={tr('floor.title')}>
      <p class="tech-label basemap-menu-title">{tr('floor.title')}</p>
      {levels.map((l) => {
        const name = (l.name && localized(l.name, getLang(), v.venue.default_language)) || l.id
        return (
          <button
            key={l.id}
            type="button"
            role="option"
            aria-selected={active === l.id}
            class={`lang-item${active === l.id ? ' lang-item-on' : ''}`}
            onClick={() => pick(l.id)}
          >
            <span>{name}</span>
            {active === l.id && <span aria-hidden="true">✓</span>}
          </button>
        )
      })}
      <button
        type="button"
        role="option"
        aria-selected={active === null}
        class={`lang-item${active === null ? ' lang-item-on' : ''}`}
        onClick={() => pick(null)}
      >
        <span>{tr('floor.all')}</span>
        {active === null && <span aria-hidden="true">✓</span>}
      </button>
    </div>
  )
}

/** Рельс инструментов: стиль карты · этажи · 3D · замер · A→B · легенда. */
export function ToolRail({ floors = false }: { floors?: boolean }) {
  langTick.value
  const [styleOpen, setStyleOpen] = useState(false)
  const [floorsOpen, setFloorsOpen] = useState(false)
  const measuring = measureActive.value
  return (
    <div class="tool-rail">
      <RailBtn
        glyph={GLYPH_LAYERS}
        title={tr('basemap.title')}
        on={styleOpen}
        onClick={() => setStyleOpen((v) => !v)}
      />
      {styleOpen && <BasemapMenu onClose={() => setStyleOpen(false)} />}
      {floors && (
        <RailBtn
          glyph={GLYPH_FLOORS}
          title={tr('floor.title')}
          on={floorsOpen || activeLevelId.value !== null}
          onClick={() => setFloorsOpen((v) => !v)}
        />
      )}
      {floorsOpen && <FloorsMenu onClose={() => setFloorsOpen(false)} />}
      <RailBtn
        glyph={GLYPH_CUBE_3D}
        title={view3D.value ? tr('map.view_2d') : tr('map.view_3d')}
        on={view3D.value}
        onClick={() => {
          view3D.value = !view3D.value
          try {
            localStorage.setItem('view3d', view3D.value ? '1' : '0')
          } catch {
            // приватный режим — выбор просто не сохранится
          }
          venueMap.value?.set3D(view3D.value)
        }}
      />
      <RailBtn
        glyph={GLYPH_RULER}
        title={tr('measure.title')}
        on={measuring}
        onClick={() => {
          const next = !measuring
          measureActive.value = next
          if (!next) {
            measurePoints.value = []
            venueMap.value?.setMeasure(null)
          }
        }}
      />
      <RailBtn
        glyph={GLYPH_A2B}
        title={tr('a2b.title')}
        on={a2bOpen.value}
        onClick={() => {
          a2bOpen.value = !a2bOpen.value
          if (!a2bOpen.value) venueMap.value?.setRoute(null)
        }}
      />
      <RailBtn
        glyph={GLYPH_CATALOG}
        title={tr('catalog.title')}
        on={catalogOpen.value}
        onClick={() => {
          catalogOpen.value = !catalogOpen.value
        }}
      />
      {/* QR всей территории: щит на входе, ссылка в переписке, экран телефона
          у стойки — один и тот же адрес /v/{venue}. */}
      <RailBtn
        glyph={GLYPH_QR}
        title={tr('qrpass.venue_open')}
        on={qrPassTargetId.value === VENUE_QR_TARGET}
        onClick={() => {
          qrPassTargetId.value = VENUE_QR_TARGET
        }}
      />
      <RailBtn
        glyph={GLYPH_LEGEND}
        title={tr('legend.title')}
        on={legendOpen.value}
        onClick={() => {
          legendOpen.value = !legendOpen.value
        }}
      />
    </div>
  )
}

/** «Кликните по зданию или точке» — до первого выбора цели. */
export function EmptyHint() {
  langTick.value
  return <p class="empty-hint">{tr('map.empty_hint')}</p>
}
